import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, Users, Calendar, CheckCircle, MessageSquare, Clock, AlertTriangle, Award, CalendarX, ChevronDown, ChevronUp } from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import LoadingSpinner from '../components/ui/LoadingSpinner';

interface SDRStats {
  user_id: string;
  sales_name: string;
  squad: string;
  jours_travailles: number;
  rdvs_pris: number;
  rdvs_effectues: number;
  rdvs_annules: number;
  rdvs_reportes?: number;
  conversations: number;
  conv_30s: number;
}

interface ComputedStats extends SDRStats {
  rdvs_passes: number;
  rdvs_planifies: number;
  taux_showup: number;
  taux_noshow: number;
  rdv_par_jour: number;
  conv_par_jour: number;
}

interface ClientSDR {
  user_id: string;
  sales_name: string;
  squad: string;
  jours: number;
  rdvs_pris: number;
  rdvs_effectues: number;
  rdvs_annules: number;
}

interface ClientStats {
  client_id: string;
  company_name: string;
  total_jours: number;
  total_rdvs_pris: number;
  total_rdvs_effectues: number;
  total_rdvs_annules: number;
  rdvs_planifies: number;
  rdvs_confirmes: number;
  rdvs_reportes: number;
  rdvs_noreply: number;
  sdrs: ClientSDR[];
}

interface ComputedClientStats extends ClientStats {
  rdvs_passes: number;
  taux_showup: number;
  taux_noshow: number;
}

type PeriodFilter = 'week' | 'month' | 'quarter' | 'all' | 'custom';
type SortKey = keyof ComputedStats;

const StatsManagerPage: React.FC = () => {
  const [stats, setStats] = useState<ComputedStats[]>([]);
  const [clientStats, setClientStats] = useState<ComputedClientStats[]>([]);
  const [viewMode, setViewMode] = useState<'sdr' | 'client'>('sdr');
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('month');
  const [selectedSDR, setSelectedSDR] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('rdvs_pris');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [customStartDate, setCustomStartDate] = useState<string>(() => {
    const now = new Date();
    return format(startOfMonth(now), 'yyyy-MM-dd');
  });
  const [customEndDate, setCustomEndDate] = useState<string>(() => {
    const now = new Date();
    return format(endOfMonth(now), 'yyyy-MM-dd');
  });
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  const getPeriodDates = (period: PeriodFilter): { start: Date; end: Date } => {
    const now = new Date();
    switch (period) {
      case 'week':
        return { start: startOfWeek(now, { locale: fr }), end: endOfWeek(now, { locale: fr }) };
      case 'month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'quarter':
        return { start: startOfQuarter(now), end: endOfQuarter(now) };
      case 'all':
        return { start: startOfYear(now), end: now };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : startOfMonth(now),
          end: customEndDate ? new Date(customEndDate) : endOfMonth(now),
        };
    }
  };

  const formatDate = (d: Date) => d.toISOString().split('T')[0];

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { start, end } = getPeriodDates(periodFilter);
      const startDateStr = formatDate(start);
      const endDateStr = formatDate(end);

      console.log('Fetching stats with dates:', { startDateStr, endDateStr });

      if (viewMode === 'sdr') {
        const { data, error } = await supabase.rpc('get_sdr_stats_public', {
          start_date: startDateStr,
          end_date: endDateStr,
        });

        if (error) {
          console.error('RPC Error:', error);
          throw error;
        }

        console.log('Raw RPC data:', data);

        const computed: ComputedStats[] = (data || []).map((row: SDRStats) => {
          const rdvFait = row.rdvs_effectues + row.rdvs_annules;
          const showUp = rdvFait > 0 ? Math.round((row.rdvs_effectues / rdvFait) * 100) : 0;
          const noShow = rdvFait > 0 ? Math.round((row.rdvs_annules / rdvFait) * 100) : 0;
          const rdvReportes = row.rdvs_reportes || 0;
          const rdvPlanifies = row.rdvs_pris - rdvFait - rdvReportes;

          return {
            ...row,
            rdvs_passes: rdvFait,
            rdvs_planifies: rdvPlanifies > 0 ? rdvPlanifies : 0,
            taux_showup: showUp,
            taux_noshow: noShow,
            rdv_par_jour: row.jours_travailles > 0 ? row.rdvs_pris / row.jours_travailles : 0,
            conv_par_jour: row.jours_travailles > 0 ? row.conversations / row.jours_travailles : 0,
          };
        });

        console.log('Computed stats:', computed);
        setStats(computed);
      } else {
        const { data, error } = await supabase.rpc('get_client_stats_public', {
          p_start: startDateStr,
          p_end: endDateStr,
        });

        if (error) {
          console.error('Client RPC Error:', error);
          throw error;
        }

        console.log('Raw client data:', data);

        if (!data || data.length === 0) {
          console.warn('No client data returned from RPC');
          setClientStats([]);
          return;
        }

        const computed: ComputedClientStats[] = (data || []).map((row: ClientStats) => {
          const rdvFait = row.total_rdvs_effectues + row.total_rdvs_annules;
          const showUp = rdvFait > 0 ? Math.round((row.total_rdvs_effectues / rdvFait) * 100) : 0;
          const noShow = rdvFait > 0 ? Math.round((row.total_rdvs_annules / rdvFait) * 100) : 0;

          return {
            ...row,
            rdvs_passes: rdvFait,
            taux_showup: showUp,
            taux_noshow: noShow,
          };
        });

        console.log('Computed client stats:', computed);
        setClientStats(computed.sort((a, b) => b.total_rdvs_pris - a.total_rdvs_pris));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [periodFilter, customStartDate, customEndDate, viewMode]);

  const handlePeriodClick = (period: PeriodFilter) => {
    setPeriodFilter(period);
    if (period !== 'custom') {
      const { start, end } = getPeriodDates(period);
      setCustomStartDate(format(start, 'yyyy-MM-dd'));
      setCustomEndDate(format(end, 'yyyy-MM-dd'));
    }
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    setPeriodFilter('custom');
  };

  const filteredStats = useMemo(() => {
    let filtered = stats;
    if (selectedSDR !== 'all') {
      filtered = filtered.filter(s => s.user_id === selectedSDR);
    }
    return filtered;
  }, [stats, selectedSDR]);

  const sortedStats = useMemo(() => {
    return [...filteredStats].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      const multiplier = sortDirection === 'asc' ? 1 : -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * multiplier;
      }
      return String(aVal).localeCompare(String(bVal)) * multiplier;
    });
  }, [filteredStats, sortKey, sortDirection]);

  const eagleStats = sortedStats.filter(s => s.squad === 'EAGLE');
  const bigBossStats = sortedStats.filter(s => s.squad === 'BIG BOSS');
  const otherStats = sortedStats.filter(s => s.squad !== 'EAGLE' && s.squad !== 'BIG BOSS');

  const globalKPIs = useMemo(() => {
    return {
      sdrs_actifs: stats.length,
      jours_travailles: stats.reduce((sum, s) => sum + s.jours_travailles, 0),
      rdvs_pris: stats.reduce((sum, s) => sum + s.rdvs_pris, 0),
      rdvs_effectues: stats.reduce((sum, s) => sum + s.rdvs_effectues, 0),
      conversations: stats.reduce((sum, s) => sum + s.conversations, 0),
      conv_30s: stats.reduce((sum, s) => sum + s.conv_30s, 0),
    };
  }, [stats]);

  const alerts = useMemo(() => {
    const lowShowup = stats.filter(s => s.rdvs_pris >= 3 && s.taux_showup < 50);
    const noDays = stats.filter(s => s.jours_travailles === 0);
    const topPerformer = stats.length > 0 ? stats.reduce((max, s) => s.rdvs_pris > max.rdvs_pris ? s : max) : null;
    return { lowShowup, noDays, topPerformer };
  }, [stats]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const uniqueSDRs = useMemo(() => {
    return stats.map(s => ({ id: s.user_id, name: s.sales_name }));
  }, [stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-[1600px] mx-auto space-y-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#EE3195]/20 rounded-xl">
              <BarChart3 className="h-8 w-8 text-[#EE3195]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Stats Manager</h1>
              <p className="text-gray-600 text-sm">Performances des SDRs</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Vue:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('sdr')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'sdr'
                      ? 'bg-[#EE3195] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Par SDR
                </button>
                <button
                  onClick={() => setViewMode('client')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'client'
                      ? 'bg-[#EE3195] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Par client
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Période:</span>
              <div className="flex gap-2">
                {[
                  { key: 'week', label: 'Cette semaine' },
                  { key: 'month', label: 'Ce mois' },
                  { key: 'quarter', label: 'Ce trimestre' },
                  { key: 'all', label: 'Tout' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => handlePeriodClick(key as PeriodFilter)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      periodFilter === key
                        ? 'bg-[#EE3195] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Du:</span>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => handleDateChange(e.target.value, customEndDate)}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-[#EE3195] focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Au:</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => handleDateChange(customStartDate, e.target.value)}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-[#EE3195] focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">SDR:</span>
              <select
                value={selectedSDR}
                onChange={(e) => setSelectedSDR(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-[#EE3195] focus:border-transparent"
              >
                <option value="all">Tous les SDRs</option>
                {uniqueSDRs.map(sdr => (
                  <option key={sdr.id} value={sdr.id}>{sdr.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { icon: Users, label: 'SDRs actifs', value: globalKPIs.sdrs_actifs, color: 'text-[#60A5FA]' },
            { icon: Calendar, label: 'Jours travaillés', value: globalKPIs.jours_travailles, color: 'text-[#4ade80]' },
            { icon: CheckCircle, label: 'RDVs pris', value: globalKPIs.rdvs_pris, color: 'text-[#EE3195]' },
            { icon: CheckCircle, label: 'RDVs effectués', value: globalKPIs.rdvs_effectues, color: 'text-[#a78bfa]' },
            { icon: MessageSquare, label: 'Conversations', value: globalKPIs.conversations, color: 'text-[#fb923c]' },
            { icon: Clock, label: 'Conv. +30s', value: globalKPIs.conv_30s, color: 'text-[#fbbf24]' },
          ].map(({ icon: Icon, label, value, color }) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
            >
              <div className="flex items-center gap-3">
                <Icon className={`h-5 w-5 ${color}`} />
                <div>
                  <p className="text-sm text-gray-600">{label}</p>
                  <p className="text-2xl font-bold font-['DM_Mono'] text-gray-900">{value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {(alerts.lowShowup.length > 0 || alerts.noDays.length > 0 || alerts.topPerformer) && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <button
              onClick={() => setShowAllAlerts(!showAllAlerts)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-[#fb923c]" />
                <span className="text-sm font-semibold text-gray-900">
                  Points d'attention ({alerts.lowShowup.length + alerts.noDays.length + (alerts.topPerformer ? 1 : 0)})
                </span>
              </div>
              {showAllAlerts ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
            <AnimatePresence>
              {showAllAlerts && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-3 space-y-2">
                    {alerts.lowShowup.map(sdr => (
                      <div key={sdr.user_id} className="flex items-center gap-2 py-2 text-sm">
                        <AlertTriangle className="h-4 w-4 text-[#f87171] flex-shrink-0" />
                        <span className="text-gray-700">
                          <span className="font-medium">{sdr.sales_name}</span> ({sdr.squad}) - Show-up {sdr.taux_showup.toFixed(0)}% sur {sdr.rdvs_pris} RDVs
                        </span>
                      </div>
                    ))}
                    {alerts.noDays.map(sdr => (
                      <div key={sdr.user_id} className="flex items-center gap-2 py-2 text-sm">
                        <CalendarX className="h-4 w-4 text-[#fb923c] flex-shrink-0" />
                        <span className="text-gray-700">
                          <span className="font-medium">{sdr.sales_name}</span> ({sdr.squad}) - Aucun jour déclaré
                        </span>
                      </div>
                    ))}
                    {alerts.topPerformer && (
                      <div className="flex items-center gap-2 py-2 text-sm">
                        <Award className="h-4 w-4 text-[#4ade80] flex-shrink-0" />
                        <span className="text-gray-700">
                          <span className="font-medium">{alerts.topPerformer.sales_name}</span> ({alerts.topPerformer.squad}) - Top performer avec {alerts.topPerformer.rdvs_pris} RDVs
                        </span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {viewMode === 'sdr' ? (
          <>
            {eagleStats.length > 0 && (
              <StatsTable
                title="EAGLE"
                stats={eagleStats}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            )}

            {bigBossStats.length > 0 && (
              <StatsTable
                title="BIG BOSS"
                stats={bigBossStats}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            )}

            {otherStats.length > 0 && (
              <StatsTable
                title="Autres"
                stats={otherStats}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            )}
          </>
        ) : (
          <ClientStatsTable
            stats={clientStats}
            expandedClients={expandedClients}
            onToggleExpand={(clientId) => {
              const newExpanded = new Set(expandedClients);
              if (newExpanded.has(clientId)) {
                newExpanded.delete(clientId);
              } else {
                newExpanded.add(clientId);
              }
              setExpandedClients(newExpanded);
            }}
          />
        )}
      </motion.div>
    </div>
  );
};

interface StatsTableProps {
  title: string;
  stats: ComputedStats[];
  sortKey: SortKey;
  sortDirection: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
}

const StatsTable: React.FC<StatsTableProps> = ({ title, stats, sortKey, sortDirection, onSort }) => {
  const totals = useMemo(() => {
    const totalRdvsPris = stats.reduce((sum, s) => sum + s.rdvs_pris, 0);
    const totalRdvsEffectues = stats.reduce((sum, s) => sum + s.rdvs_effectues, 0);
    const totalRdvsAnnules = stats.reduce((sum, s) => sum + s.rdvs_annules, 0);
    const totalRdvFait = totalRdvsEffectues + totalRdvsAnnules;
    const totalRdvsPasses = stats.reduce((sum, s) => sum + s.rdvs_passes, 0);
    const totalRdvsPlanifies = stats.reduce((sum, s) => sum + s.rdvs_planifies, 0);
    const totalRdvsReportes = stats.reduce((sum, s) => sum + (s.rdvs_reportes || 0), 0);

    return {
      jours_travailles: stats.reduce((sum, s) => sum + s.jours_travailles, 0),
      rdvs_pris: totalRdvsPris,
      rdvs_effectues: totalRdvsEffectues,
      rdvs_annules: totalRdvsAnnules,
      rdvs_passes: totalRdvsPasses,
      rdvs_planifies: totalRdvsPlanifies,
      rdvs_reportes: totalRdvsReportes,
      conversations: stats.reduce((sum, s) => sum + s.conversations, 0),
      conv_30s: stats.reduce((sum, s) => sum + s.conv_30s, 0),
      rdv_par_jour: stats.reduce((sum, s) => sum + s.rdv_par_jour, 0) / stats.length,
      conv_par_jour: stats.reduce((sum, s) => sum + s.conv_par_jour, 0) / stats.length,
      taux_showup: totalRdvFait > 0 ? Math.round((totalRdvsEffectues / totalRdvFait) * 100) : 0,
      taux_noshow: totalRdvFait > 0 ? Math.round((totalRdvsAnnules / totalRdvFait) * 100) : 0,
    };
  }, [stats]);

  const getShowupTextColor = (rate: number) => {
    if (rate >= 70) return 'text-[#4ade80]';
    if (rate >= 50) return 'text-[#fbbf24]';
    return 'text-[#f87171]';
  };

  const getNoshowTextColor = (rate: number) => {
    if (rate < 15) return 'text-[#4ade80]';
    if (rate < 30) return 'text-[#fbbf24]';
    return 'text-[#f87171]';
  };

  const getSquadAvatarColor = (squad: string) => {
    if (squad === 'EAGLE') return 'bg-[#0f2340] text-[#60A5FA]';
    if (squad === 'BIG BOSS') return 'bg-[#1e293b] text-[#94a3b8]';
    return 'bg-[#1a1e2e] text-[#e2e8f0]';
  };

  const SortableHeader: React.FC<{ label: string; sortKey: SortKey }> = ({ label, sortKey: key }) => (
    <th
      onClick={() => onSort(key)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-[#EE3195] transition-colors"
    >
      <div className="flex items-center gap-1">
        {label}
        {sortKey === key && (
          <span className="text-[#EE3195]">{sortDirection === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
              <SortableHeader label="SDR" sortKey="sales_name" />
              <SortableHeader label="Jours" sortKey="jours_travailles" />
              <SortableHeader label="RDVs pris" sortKey="rdvs_pris" />
              <SortableHeader label="RDV/j" sortKey="rdv_par_jour" />
              <SortableHeader label="Conv." sortKey="conversations" />
              <SortableHeader label="+30s" sortKey="conv_30s" />
              <SortableHeader label="Conv./j" sortKey="conv_par_jour" />
              <SortableHeader label="Effectués" sortKey="rdvs_effectues" />
              <SortableHeader label="Annulés" sortKey="rdvs_annules" />
              <SortableHeader label="Passés" sortKey="rdvs_passes" />
              <SortableHeader label="Planifiés" sortKey="rdvs_planifies" />
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reportés</th>
              <SortableHeader label="Show-up" sortKey="taux_showup" />
              <SortableHeader label="No-show" sortKey="taux_noshow" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {stats.map((sdr, index) => (
              <tr key={sdr.user_id} className="bg-white hover:bg-gray-50 transition-colors">
                <td className="px-4 py-4 text-sm text-gray-500 font-['DM_Mono']">{index + 1}</td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full ${getSquadAvatarColor(sdr.squad)} flex items-center justify-center text-xs font-bold`}>
                      {sdr.sales_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{sdr.sales_name}</p>
                      <p className="text-xs text-gray-500">{sdr.squad}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{sdr.jours_travailles}</td>
                <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono'] font-semibold">{sdr.rdvs_pris}</td>
                <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{sdr.rdv_par_jour.toFixed(1)}</td>
                <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{sdr.conversations}</td>
                <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{sdr.conv_30s}</td>
                <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{sdr.conv_par_jour.toFixed(1)}</td>
                <td className="px-4 py-4 text-sm text-[#4ade80] font-['DM_Mono']">{sdr.rdvs_effectues}</td>
                <td className="px-4 py-4 text-sm text-[#fb923c] font-['DM_Mono']">{sdr.rdvs_annules}</td>
                <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{sdr.rdvs_passes}</td>
                <td className="px-4 py-4 text-sm text-[#60a5fa] font-['DM_Mono']">{sdr.rdvs_planifies}</td>
                <td className="px-4 py-4 text-sm text-[#fbbf24] font-['DM_Mono']">{sdr.rdvs_reportes || 0}</td>
                <td className="px-4 py-4 bg-gray-100">
                  <div className="flex items-center justify-center">
                    <span className={`text-sm font-bold font-['DM_Mono'] ${getShowupTextColor(sdr.taux_showup)}`}>
                      {sdr.taux_showup.toFixed(0)}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4 bg-gray-100">
                  <div className="flex items-center justify-center">
                    <span className={`text-sm font-bold font-['DM_Mono'] ${getNoshowTextColor(sdr.taux_noshow)}`}>
                      {sdr.taux_noshow.toFixed(0)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              <td colSpan={2} className="px-4 py-4 text-sm text-gray-900">TOTAL</td>
              <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{totals.jours_travailles}</td>
              <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{totals.rdvs_pris}</td>
              <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{totals.rdv_par_jour.toFixed(1)}</td>
              <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{totals.conversations}</td>
              <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{totals.conv_30s}</td>
              <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{totals.conv_par_jour.toFixed(1)}</td>
              <td className="px-4 py-4 text-sm text-[#4ade80] font-['DM_Mono']">{totals.rdvs_effectues}</td>
              <td className="px-4 py-4 text-sm text-[#fb923c] font-['DM_Mono']">{totals.rdvs_annules}</td>
              <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{totals.rdvs_passes}</td>
              <td className="px-4 py-4 text-sm text-[#60a5fa] font-['DM_Mono']">{totals.rdvs_planifies}</td>
              <td className="px-4 py-4 text-sm text-[#fbbf24] font-['DM_Mono']">{totals.rdvs_reportes}</td>
              <td className="px-4 py-4 bg-gray-100">
                <div className="flex items-center justify-center">
                  <span className={`text-sm font-bold font-['DM_Mono'] ${getShowupTextColor(totals.taux_showup)}`}>
                    {totals.taux_showup.toFixed(0)}%
                  </span>
                </div>
              </td>
              <td className="px-4 py-4 bg-gray-100">
                <div className="flex items-center justify-center">
                  <span className={`text-sm font-bold font-['DM_Mono'] ${getNoshowTextColor(totals.taux_noshow)}`}>
                    {totals.taux_noshow.toFixed(0)}%
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface ClientStatsTableProps {
  stats: ComputedClientStats[];
  expandedClients: Set<string>;
  onToggleExpand: (clientId: string) => void;
}

type ClientSortKey = keyof ComputedClientStats;

const ClientStatsTable: React.FC<ClientStatsTableProps> = ({ stats, expandedClients, onToggleExpand }) => {
  const [sortKey, setSortKey] = useState<ClientSortKey>('total_rdvs_pris');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortedStats = useMemo(() => {
    return [...stats].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      const multiplier = sortDirection === 'asc' ? 1 : -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * multiplier;
      }
      return String(aVal).localeCompare(String(bVal)) * multiplier;
    });
  }, [stats, sortKey, sortDirection]);

  const handleSort = (key: ClientSortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const getShowupTextColor = (rate: number) => {
    if (rate >= 70) return 'text-[#4ade80]';
    if (rate >= 50) return 'text-[#fbbf24]';
    return 'text-[#f87171]';
  };

  const getNoshowTextColor = (rate: number) => {
    if (rate < 15) return 'text-[#4ade80]';
    if (rate < 30) return 'text-[#fbbf24]';
    return 'text-[#f87171]';
  };

  const SortableHeader: React.FC<{ label: string; sortKey: ClientSortKey }> = ({ label, sortKey: key }) => (
    <th
      onClick={() => handleSort(key)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-[#EE3195] transition-colors"
    >
      <div className="flex items-center gap-1">
        {label}
        {sortKey === key && (
          <span className="text-[#EE3195]">{sortDirection === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Stats par client</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-10"></th>
              <SortableHeader label="Client" sortKey="company_name" />
              <SortableHeader label="Jours" sortKey="total_jours" />
              <SortableHeader label="RDVs pris" sortKey="total_rdvs_pris" />
              <SortableHeader label="Effectués" sortKey="total_rdvs_effectues" />
              <SortableHeader label="Annulés" sortKey="total_rdvs_annules" />
              <SortableHeader label="Planifiés" sortKey="rdvs_planifies" />
              <SortableHeader label="Reportés" sortKey="rdvs_reportes" />
              <SortableHeader label="Show-up" sortKey="taux_showup" />
              <SortableHeader label="No-show" sortKey="taux_noshow" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedStats.map((client) => {
              const isExpanded = expandedClients.has(client.client_id);
              return (
                <React.Fragment key={client.client_id}>
                  <tr className="bg-white hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => onToggleExpand(client.client_id)}>
                    <td className="px-4 py-4">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-gray-500 rotate-180" />
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{client.company_name}</td>
                    <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono']">{client.total_jours}</td>
                    <td className="px-4 py-4 text-sm text-gray-900 font-['DM_Mono'] font-semibold">{client.total_rdvs_pris}</td>
                    <td className="px-4 py-4 text-sm text-[#4ade80] font-['DM_Mono']">{client.total_rdvs_effectues}</td>
                    <td className="px-4 py-4 text-sm text-[#fb923c] font-['DM_Mono']">{client.total_rdvs_annules}</td>
                    <td className="px-4 py-4 text-sm text-[#60a5fa] font-['DM_Mono']">{client.rdvs_planifies}</td>
                    <td className="px-4 py-4 text-sm text-[#fbbf24] font-['DM_Mono']">{client.rdvs_reportes}</td>
                    <td className="px-4 py-4 bg-gray-100">
                      <div className="flex items-center justify-center">
                        <span className={`text-sm font-bold font-['DM_Mono'] ${getShowupTextColor(client.taux_showup)}`}>
                          {client.taux_showup}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 bg-gray-100">
                      <div className="flex items-center justify-center">
                        <span className={`text-sm font-bold font-['DM_Mono'] ${getNoshowTextColor(client.taux_noshow)}`}>
                          {client.taux_noshow}%
                        </span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && client.sdrs.map((sdr, idx) => {
                    const sdrRdvFait = sdr.rdvs_effectues + sdr.rdvs_annules;
                    const sdrShowUp = sdrRdvFait > 0 ? Math.round((sdr.rdvs_effectues / sdrRdvFait) * 100) : 0;
                    const sdrNoShow = sdrRdvFait > 0 ? Math.round((sdr.rdvs_annules / sdrRdvFait) * 100) : 0;

                    return (
                      <tr key={`${client.client_id}-${sdr.user_id}`} className="bg-gray-50 hover:bg-gray-100 transition-colors">
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 pl-12">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                              {sdr.sales_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <div>
                              <p className="text-xs text-gray-700">{sdr.sales_name}</p>
                              <p className="text-xs text-gray-500">{sdr.squad}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700 font-['DM_Mono']">{sdr.jours}</td>
                        <td className="px-4 py-3 text-xs text-gray-700 font-['DM_Mono']">{sdr.rdvs_pris}</td>
                        <td className="px-4 py-3 text-xs text-[#4ade80] font-['DM_Mono']">{sdr.rdvs_effectues}</td>
                        <td className="px-4 py-3 text-xs text-[#fb923c] font-['DM_Mono']">{sdr.rdvs_annules}</td>
                        <td className="px-4 py-3 text-xs text-[#60a5fa] font-['DM_Mono']">-</td>
                        <td className="px-4 py-3 text-xs text-[#fbbf24] font-['DM_Mono']">-</td>
                        <td className="px-4 py-3 bg-gray-50">
                          <div className="flex items-center justify-center">
                            <span className={`text-xs font-bold font-['DM_Mono'] ${getShowupTextColor(sdrShowUp)}`}>
                              {sdrShowUp}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 bg-gray-50">
                          <div className="flex items-center justify-center">
                            <span className={`text-xs font-bold font-['DM_Mono'] ${getNoshowTextColor(sdrNoShow)}`}>
                              {sdrNoShow}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StatsManagerPage;
